// Reports → Price changes (decision 161): every tier price that moved in a period, all products, newest first, with the
// price before, the cost it was set on, and who set it ("auto" when it followed cost by itself). JL's sheet kept this
// as PRICE HISTORY.
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Empty, QueryView } from '../components/ui';
import { formatDate, formatMoney, todayIso } from '../format';
import { useSearchParam } from '../hooks';
import { useLocale } from '../i18n';
import { qk } from '../queryClient';

const daysBefore = (iso: string, n: number) => new Date(Date.parse(`${iso}T12:00:00Z`) - n * 86_400_000).toISOString().slice(0, 10);

export function PriceChangesPage() {
  const { t, intlLocale } = useLocale();
  const today = todayIso();
  const [fromRaw, setFrom] = useSearchParam('from');
  const [toRaw, setTo] = useSearchParam('to');
  const to = toRaw || today;
  const from = fromRaw || daysBefore(to, 13);
  const query = useQuery({ queryKey: qk.priceChanges(from, to), queryFn: ({ signal }) => api.priceChanges(from, to, signal) });
  const money = (v: number | null) => (v === null ? '—' : formatMoney(v, intlLocale));
  return (
    <div className="desk-page stack" data-testid="price-changes">
      <header className="desk-head">
        <div>
          <h1>{t('priceChangesTitle')}</h1>
          <p className="muted">{t('priceChangesHint')}</p>
        </div>
      </header>
      <div className="toolbar">
        <label className="tool">
          <span className="label">{t('fromDate')}</span>
          <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        </label>
        <label className="tool">
          <span className="label">{t('toDate')}</span>
          <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        </label>
      </div>
      <QueryView query={query}>
        {(data) =>
          data.rows.length === 0 ? (
            <Empty>{t('priceChangesNone')}</Empty>
          ) : (
            <>
              <div className="table-wrap">
                <table className="data-table" aria-label={t('priceChangesTitle')}>
                  <thead>
                    <tr>
                      <th>{t('phDate')}</th>
                      <th>{t('itemCol')}</th>
                      <th>{t('tierCol')}</th>
                      <th className="num">{t('priceBeforeCol')}</th>
                      <th className="num">{t('priceNowCol')}</th>
                      <th className="num">{t('phCostBasis')}</th>
                      <th>{t('phBy')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.rows.map((r, i) => (
                      <tr key={`${r.itemId}-${r.tierCode}-${r.at}-${i}`}>
                        <td>{formatDate(r.date, intlLocale, 'date')}</td>
                        <td>
                          <Link to={`/items/${r.itemId}/history`}>{r.itemName}</Link>
                        </td>
                        <td>{r.tierName}</td>
                        <td className="num">{money(r.from)}</td>
                        <td className="num">
                          {r.from !== null && r.to !== r.from ? <span className={r.to > r.from ? 'move-up' : 'move-down'}>{r.to > r.from ? '▲ ' : '▼ '}</span> : null}
                          {money(r.to)}
                        </td>
                        <td className="num">{money(r.costBasis)}</td>
                        <td>
                          {r.by === 'auto' ? t('priceByAuto') : (r.by ?? '')}
                          {r.mode ? <span className="cell-sub">{t(r.mode === 'auto' ? 'priceAuto' : 'priceHand')}</span> : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.truncated ? <p className="note">{t('priceChangesTruncated')}</p> : null}
            </>
          )
        }
      </QueryView>
    </div>
  );
}
